import { AXIS_MAX, Action, TICK_RATE, type CharacterManifest, type InputCommand, type Placement } from "@arena/shared";

/**
 * Cleopatra (docs/personaggi/cleopatra.md): the last queen of Egypt as a
 * Hollywood femme fatale, over the top and ironic like the rest of the
 * roster: she charmed Caesar and Mark Antony, and treats everyone else as
 * next in line. A golden sceptre crowned with the uraeus; S1 lets her asp out
 * of her bag to slither along the floor (a jump clears it) and poison; S2 is
 * a bath of asses' milk that restores her and her allies and washes the venom
 * away; the Super Slam is her famous nose and a blown kiss: whoever stands
 * near loses their head for three seconds, runs after her and cannot attack.
 */
export const CLEOPATRA: CharacterManifest = {
  id: "cleopatra",
  schemaVersion: 2,
  displayName: "Cleopatra",
  archetype: "trickster",
  description: "Donna fatale di professione: scettro d’oro, un aspide in borsetta e un bagno di latte d’asina che rimette in forma lei e i suoi. Suprema: un bacio soffiato, e per tre secondi chi le sta vicino perde la testa e le corre dietro.",
  look: { build: "slim", accent: "#3a64c8" },
  stats: { speed: 55, power: 40, resistance: 45, weight: 30, range: 60, slamCharge: 60 },
  moveset: {
    light1: { name: "Schiaffetto regale", startup: 4, active: 2, recovery: 9, range: 1.4, arcDeg: 90, knockback: 2.5, lift: 0, hitstun: 13, slamGain: 6, lunge: 2.5, cancelFrom: 6, nextLight: Action.Light2, superCancel: true, specialCancel: true, damage: 6 },
    light2: { name: "Ventaglio, tesoro", startup: 4, active: 3, recovery: 10, range: 1.6, arcDeg: 150, knockback: 3, lift: 0, hitstun: 14, slamGain: 6, lunge: 2, cancelFrom: 7, nextLight: Action.Light3, superCancel: true, specialCancel: true, damage: 7 },
    light3: { name: "Morso dell’ureo", startup: 7, active: 2, recovery: 14, range: 1.9, arcDeg: 70, knockback: 8, lift: 4, hitstun: 20, slamGain: 10, lunge: 3.5, cancelFrom: 255, nextLight: Action.None, superCancel: true, damage: 11 },
    airAttack: { name: "Tuffo nel Nilo", startup: 5, active: 4, recovery: 12, range: 1.6, arcDeg: 360, knockback: 5, lift: 2, hitstun: 14, slamGain: 7, lunge: 0, cancelFrom: 12, nextLight: Action.None, superCancel: true, specialCancel: true, damage: 9 },
    dashAttack: { name: "Passerella reale", startup: 4, active: 4, recovery: 12, range: 1.6, arcDeg: 110, knockback: 6, lift: 1.5, hitstun: 16, slamGain: 8, lunge: 5, travel: 7, cancelFrom: 10, nextLight: Action.None, superCancel: true, specialCancel: true, damage: 10 },
    special1: {
      name: "Ti presento l’aspide",
      // Crawls on the floor and slowly: readable, and a jump clears it.
      startup: 10, active: 1, recovery: 14, range: 0, arcDeg: 360, knockback: 1.5, lift: 0, hitstun: 10, slamGain: 6, lunge: 0,
      cancelFrom: 255, nextLight: Action.None, superCancel: true, cooldown: 6 * TICK_RATE, damage: 4,
      projectile: { speed: 9, lifetime: 45, radius: 0.45, height: 0.3, ground: true },
    },
    special2: {
      name: "Bagno di latte d’asina",
      startup: 12, active: 1, recovery: 12, range: 0, arcDeg: 360, knockback: 0, lift: 0, hitstun: 0, slamGain: 0, lunge: 0,
      cancelFrom: 255, nextLight: Action.None, superCancel: false, damage: 0, cooldown: 15 * TICK_RATE, groundOnly: true,
    },
    superSlam: {
      name: "Il naso di Cleopatra",
      // The range is the charm's: the wind-up ring shows who will be caught.
      startup: 12, active: 1, recovery: 16, armorTicks: 12, range: 5, arcDeg: 360, knockback: 0, lift: 0, hitstun: 0, slamGain: 0, lunge: 0,
      cancelFrom: 255, nextLight: Action.None, superCancel: false, damage: 0,
    },
  },
};

/**
 * The asp's venom: `damage` every `every` ticks for `ticks` (Arena KO), slowed
 * for `slowTicks`; in Slam each bite of the venom feeds her meter instead.
 */
export const CLEOPATRA_POISON = { ticks: 3 * TICK_RATE, every: 15, damage: 4, slowTicks: 45, slamGain: 2 } as const;

/** The bath: `radius` m round where she stood, for `ticks`; every `every` ticks she and her allies inside get `hp` (Arena KO) or `slam`. */
export const CLEOPATRA_BATH = { radius: 2.2, ticks: 4 * TICK_RATE, every: 15, hp: 6, slam: 4 } as const;

/** The nose: enemies within `radius` are charmed for `ticks`; they walk to her and stop `close` to her. */
export const CLEOPATRA_CHARM = { radius: 5, ticks: 3 * TICK_RATE, close: 1.2 } as const;

/**
 * What a charmed fighter does in a step, whatever its player presses: it walks
 * to its queen and stops before her, without a button. Server and client use
 * the same rule, so the prediction does not fight the charm.
 */
export function charmedCommand(self: Placement, queen: Placement | null): InputCommand {
  if (!queen) return { mx: 0, mz: 0, buttons: 0 };
  const dx = queen.x - self.x;
  const dz = queen.z - self.z;
  const distance = Math.hypot(dx, dz);
  if (distance < CLEOPATRA_CHARM.close) return { mx: 0, mz: 0, buttons: 0 };
  return { mx: Math.round((dx / distance) * AXIS_MAX), mz: Math.round((dz / distance) * AXIS_MAX), buttons: 0 };
}
